'use client';

import Image from 'next/image';
import Link from 'next/link';
import { motion } from 'motion/react';
import { ShieldCheck, Clock, Truck, Tv, Smartphone, MonitorPlay, CheckCircle2, ChevronDown, PlayCircle, MessageCircle } from 'lucide-react';
import { products, faqs } from '@/lib/data';
import { useState } from 'react';

export default function Home() {
  const subscriptions = products.filter(p => p.category === 'subscription');
  const firestickPromo = products.find(p => p.category === 'hardware');

  const [openFaq, setOpenFaq] = useState<number | null>(0);

  return (
    <div className="flex flex-col">
      {/* 2. Cinematic Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-[#0a0a0a]">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#7aaace]/20 rounded-full blur-[120px] mix-blend-screen"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#1b3c53]/40 rounded-full blur-[120px] mix-blend-screen"></div>
          <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-overlay"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="space-y-6 lg:space-y-8 pt-16 lg:pt-0"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                <span className="text-xs font-medium text-gray-300 tracking-wider uppercase">Site Officiel 2024</span>
              </div>
              
              <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-[1.1]">
                Le Futur du <br/>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#7aaace] to-[#1b3c53]">
                  Divertissement
                </span>
              </h1>
              
              <p className="text-lg md:text-xl text-gray-400 max-w-lg leading-relaxed">
                Découvrez plus de 80 000 chaînes en direct et 100 000 VODs en qualité 4K époustouflante. Zéro coupure, fiabilité maximale.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  href="#subscriptions"
                  className="inline-flex items-center justify-center px-8 py-4 rounded-xl bg-white text-black font-semibold hover:scale-105 hover:shadow-[0_0_30px_rgba(255,255,255,0.3)] transition-all duration-300"
                >
                  Voir les Abonnements
                </Link>
                <a 
                  href="https://wa.me/33745561646"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-8 py-4 rounded-xl bg-[#25D366]/10 text-[#25D366] font-semibold border border-[#25D366]/20 hover:bg-[#25D366]/20 transition-all duration-300 gap-2"
                >
                  <MessageCircle className="w-5 h-5" /> contacter with whatsapp
                </a>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.2 }}
              className="relative block w-full mt-4 lg:mt-0 pb-12 lg:pb-0"
            >
              <div className="relative w-full sm:w-[80vw] lg:w-full aspect-square sm:aspect-video lg:aspect-square max-w-sm sm:max-w-2xl lg:max-w-md mx-auto">
                <motion.div 
                  animate={{ y: [-10, 10, -10] }}
                  transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute inset-0"
                >
                  <Image 
                    src="/img/unnamed.webp" 
                    alt="Aperçu de la plateforme" 
                    fill 
                    className="object-contain"
                    sizes="(max-width: 768px) 100vw, 50vw"
                    priority
                  />
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 3. Floating Trust Bar */}
      <div className="relative z-20 -mt-12 max-w-5xl mx-auto px-4 sm:px-6 w-full">
        <div className="bg-[#111]/80 backdrop-blur-xl border border-white/10 rounded-2xl p-4 sm:p-6 shadow-2xl">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 divide-y sm:divide-y-0 sm:divide-x divide-white/10">
            <div className="flex items-center gap-3 sm:gap-4 sm:px-4 lg:px-6 pt-4 sm:pt-0 first:pt-0">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#7aaace]/10 flex items-center justify-center shrink-0">
                <Truck className="w-5 h-5 sm:w-6 sm:h-6 text-[#7aaace]" />
              </div>
              <div>
                <h3 className="font-semibold text-white text-[clamp(0.875rem,2vw,1rem)]">Livraison Instantanée</h3>
                <p className="text-gray-400 text-[clamp(0.75rem,1.5vw,0.875rem)]">Par email en 5 mins</p>
              </div>
            </div>
            <div className="flex items-center gap-3 sm:gap-4 sm:px-4 lg:px-6 pt-4 sm:pt-0">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#1b3c53]/20 flex items-center justify-center shrink-0">
                <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-[#7aaace]" />
              </div>
              <div>
                <h3 className="font-semibold text-white text-[clamp(0.875rem,2vw,1rem)]">Support 24/7</h3>
                <p className="text-gray-400 text-[clamp(0.75rem,1.5vw,0.875rem)]">Toujours là pour vous aider</p>
              </div>
            </div>
            <div className="flex items-center gap-3 sm:gap-4 sm:px-4 lg:px-6 pt-4 sm:pt-0 sm:col-span-2 lg:col-span-1">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-green-500/10 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-5 h-5 sm:w-6 sm:h-6 text-green-400" />
              </div>
              <div>
                <h3 className="font-semibold text-white text-[clamp(0.875rem,2vw,1rem)]">Paiement Sécurisé</h3>
                <p className="text-gray-400 text-[clamp(0.75rem,1.5vw,0.875rem)]">Paiement 100% sûr</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Unified Subscriptions Grid */}
      <section id="subscriptions" className="py-24 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 text-white">Nos Abonnements</h2>
            <p className="text-gray-400">Choisissez le forfait qui correspond le mieux à vos besoins.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {subscriptions.map((plan) => (
              <motion.div 
                key={plan.id}
                whileHover={{ y: -5 }}
                className={`bg-[#111] border ${plan.popular ? 'border-[#7aaace]' : 'border-white/10'} rounded-3xl p-6 flex flex-col group relative`}
              >
                {plan.popular && (
                  <div className="absolute top-0 right-0 p-4 z-10">
                    <span className="bg-[#7aaace]/20 text-[#7aaace] text-[10px] font-bold px-2 py-1 rounded-full border border-[#7aaace]/20 uppercase">
                      Populaire
                    </span>
                  </div>
                )}
                <div className="relative w-full aspect-square mb-6 overflow-hidden rounded-2xl bg-[#1a1a1a]">
                  <Image 
                    src={plan.image} 
                    alt={`Image pour ${plan.name}`} 
                    fill 
                    className="object-cover group-hover:scale-105 transition-transform duration-500"
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 25vw"
                  />
                </div>
                <h3 className="text-xl font-bold text-white mb-1">{plan.duration}</h3>
                <p className="text-gray-400 text-sm mb-6 min-h-[40px]">{plan.name}</p>
                
                <div className="mb-6">
                  <span className="text-4xl font-bold text-white">{plan.price}€</span>
                </div>

                <ul className="space-y-3 mb-8 flex-1">
                  {plan.features.slice(0, 4).map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-gray-300 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-[#7aaace] shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-auto flex flex-col gap-2">
                  <Link 
                    href={`/product/${plan.id}`}
                    className={`w-full py-3 text-center rounded-xl font-medium transition-colors ${plan.popular ? 'bg-[#7aaace] text-white hover:bg-[#6898bc]' : 'bg-white/5 text-white border border-white/10 hover:bg-white/10'}`}
                  >
                    Acheter
                  </Link>
                  <a
                    href={`https://wa.me/33745561646?text=Bonjour,%20je%20souhaite%20commander%20l'${encodeURIComponent(plan.name)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full py-3 flex items-center justify-center gap-2 rounded-xl font-medium transition-colors bg-[#25D366]/10 text-[#25D366] border border-[#25D366]/20 hover:bg-[#25D366]/20"
                  >
                    <MessageCircle className="w-4 h-4" /> WhatsApp
                  </a>
                </div>
                <div className="absolute -inset-px bg-gradient-to-b from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none rounded-3xl"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. Feature Highlights Grid */}
      <section className="py-24 bg-[#0f0f0f] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { title: '99.9% de Disponibilité', desc: 'La technologie anti-coupure assure un streaming fluide.', icon: ShieldCheck },
              { title: '80K+ Chaînes', desc: 'Couverture mondiale dans toutes les catégories.', icon: Tv },
              { title: 'Bibliothèque VOD', desc: 'Derniers films et séries mis à jour quotidiennement.', icon: MonitorPlay },
              { title: 'Multi-Appareils', desc: 'Regardez sur TV, PC, Tablette ou Smartphone.', icon: Smartphone },
            ].map((feature, i) => (
              <div key={i} className="p-6 rounded-2xl bg-white/5 border border-white/5">
                <feature.icon className="w-8 h-8 text-[#7aaace] mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. "Why Choose Us" Showcase */}
      <section className="py-24 bg-[#0a0a0a] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#111] to-[#0a0a0a] border border-white/10 rounded-[2.5rem] p-8 md:p-16 relative overflow-hidden">
            {/* Abstract background shape */}
            <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#7aaace]/10 to-transparent blur-3xl pointer-events-none"></div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center relative z-10">
              <div className="space-y-8">
                <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-white">
                  Pourquoi se contenter de moins quand on peut avoir <span className="text-[#7aaace]">le meilleur ?</span>
                </h2>
                <p className="text-gray-400 text-lg">
                  Nous avons construit notre infrastructure de zéro pour fournir l'expérience IPTV la plus stable et complète du marché.
                </p>
                <ul className="space-y-4">
                  {[
                    'Serveurs dédiés privés (Pas de partage)',
                    'EPG Automatique (Guide des programmes)',
                    'Catch-up TV (Jusqu\'à 7 jours de replay)',
                    'Activation instantanée après paiement'
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-gray-300">
                      <div className="w-6 h-6 rounded-full bg-[#7aaace]/20 flex items-center justify-center shrink-0">
                        <CheckCircle2 className="w-4 h-4 text-[#7aaace]" />
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="relative h-[400px] w-full hidden lg:block">
                {/* Device Mockups Collage */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full">
                  <div className="absolute inset-0 bg-white/5 rounded-2xl border border-white/10 backdrop-blur-sm transform rotate-3 scale-105"></div>
                  <div className="absolute inset-0 bg-white/10 rounded-2xl border border-white/20 backdrop-blur-md overflow-hidden shadow-2xl">
                     <Image 
                       src="/img/ImageProduct_87133_675c7a056c116.jpg" 
                       alt="Interface de l'Application" 
                       fill 
                       className="object-cover opacity-80" 
                       sizes="(max-width: 768px) 100vw, 50vw"
                       loading="lazy"
                     />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 8. Hardware Promo (FireStick) */}
      {firestickPromo && (
        <section className="py-24 relative overflow-hidden bg-black">
          <div className="absolute inset-0">
            <Image src={firestickPromo.image} alt="Fond FireStick" fill className="object-cover opacity-30" referrerPolicy="no-referrer" />
            <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent"></div>
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-xl space-y-8">
              <div className="inline-block px-3 py-1 bg-orange-500/20 border border-orange-500/50 text-orange-400 rounded-full text-sm font-bold tracking-wider uppercase">
                Pack Matériel
              </div>
              <h2 className="text-4xl md:text-6xl font-bold text-white leading-tight">
                Divertissement <br/>Prêt à l'emploi.
              </h2>
              <p className="text-xl text-gray-300">
                {firestickPromo.description}
              </p>
              <div className="flex items-baseline gap-2">
                <span className="text-gray-400 text-lg">À partir de</span>
                <span className="text-5xl font-bold text-white">{firestickPromo.price}€</span>
              </div>
              <Link 
                href={`/product/${firestickPromo.id}`}
                className="inline-block px-8 py-4 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-xl transition-colors shadow-[0_0_30px_rgba(249,115,22,0.3)]"
              >
                Obtenir le Pack
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* 9. How it Works (Steps) */}
      <section id="how-it-works" className="py-24 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Comment ça marche</h2>
            <p className="text-gray-400">Commencez à regarder en trois étapes simples.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            {/* Connecting line for desktop */}
            <div className="hidden md:block absolute top-1/2 left-1/6 right-1/6 h-0.5 bg-gradient-to-r from-[#7aaace]/0 via-[#7aaace]/50 to-[#7aaace]/0 -translate-y-1/2 z-0"></div>
            
            {[
              { step: '01', title: 'Choisissez un Forfait', desc: 'Sélectionnez l\'abonnement qui correspond à vos besoins.' },
              { step: '02', title: 'Recevez le Code', desc: 'Obtenez vos identifiants instantanément par email.' },
              { step: '03', title: 'Commencez à Regarder', desc: 'Entrez les détails dans notre application et profitez.' },
            ].map((item, i) => (
              <div key={i} className="relative z-10 flex flex-col items-center text-center p-6 bg-[#111] rounded-2xl border border-white/5">
                <div className="w-16 h-16 rounded-full bg-[#7aaace]/10 border border-[#7aaace]/20 flex items-center justify-center text-xl font-bold text-[#7aaace] mb-6">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 10. FAQ Accordion */}
      <section id="faq" className="py-24 bg-[#0f0f0f] border-t border-white/5">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Questions Fréquemment Posées</h2>
            <p className="text-gray-400">Tout ce que vous devez savoir sur le service.</p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div 
                key={i} 
                className="bg-[#111] border border-white/5 rounded-xl overflow-hidden"
              >
                <button 
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left focus:outline-none"
                >
                  <span className="font-medium text-white">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                <motion.div 
                  initial={false}
                  animate={{ height: openFaq === i ? 'auto' : 0, opacity: openFaq === i ? 1 : 0 }}
                  className="overflow-hidden"
                >
                  <div className="px-6 pb-4 text-gray-400 text-sm leading-relaxed">
                    {faq.answer}
                  </div>
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
